import { z } from 'zod';
/**
 * Contratos de autenticação do painel.
 *
 * Estes schemas descrevem o **JSON que trafega na rede**, não os tipos do Prisma.
 * `DateTime` do Prisma chega ao navegador como string ISO: modelar uma data como
 * `z.date()` compilaria e só quebraria em runtime, dentro do `schema.parse()` do
 * `apiRequest`, com o painel já publicado.
 */
/** Papéis com acesso ao painel. `EDITOR` mexe em conteúdo; `ADMIN` também em usuários. */
export const userRoleSchema = z.enum(['ADMIN', 'EDITOR']);
/**
 * Teto de tamanho da senha. Sem ele, um corpo de requisição gigante vira trabalho
 * de derivação de chave de graça para quem ataca.
 */
const PASSWORD_MAX_LENGTH = 128;
const passwordTooLong = `A senha tem no máximo ${PASSWORD_MAX_LENGTH} caracteres.`;
/**
 * Credenciais de acesso.
 *
 * Não aplica política de senha de propósito: o login precisa aceitar a senha que a
 * pessoa já tem, inclusive depois de a política ficar mais exigente. Validar o
 * mínimo aqui só produziria conta legítima impedida de entrar.
 */
export const loginSchema = z.object({
    email: z
        .string()
        .min(1, 'Informe o e-mail.')
        .max(190, 'E-mail longo demais.')
        .pipe(z.email('E-mail inválido.')),
    password: z.string().min(1, 'Informe a senha.').max(PASSWORD_MAX_LENGTH, passwordTooLong),
});
/**
 * Política aplicada ao **definir** uma senha — criação de conta e troca de senha.
 * Nunca no login.
 */
export const passwordPolicySchema = z
    .string()
    .min(12, 'A senha tem no mínimo 12 caracteres.')
    .max(PASSWORD_MAX_LENGTH, passwordTooLong);
/**
 * Usuário como o painel o enxerga.
 *
 * `passwordHash` não existe neste contrato: o hash não tem como vazar por nenhum
 * caminho que passe por aqui. A barreira equivalente do lado do servidor é o
 * `omit` global na construção do PrismaClient.
 *
 * `email` é `z.string()` e não `z.email()` porque schema de resposta deve ser
 * tolerante quanto ao formato do que a API legitimamente guarda — a validação
 * estrita é trabalho da entrada, em `loginSchema`.
 */
export const authUserSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    role: userRoleSchema,
    lastLoginAt: z.iso.datetime().nullable(),
});
/**
 * Resposta de `POST /auth/login` e `POST /auth/refresh`.
 *
 * O refresh token não aparece aqui: ele viaja exclusivamente no cookie httpOnly,
 * fora do alcance do JavaScript.
 */
export const loginResponseSchema = z.object({
    accessToken: z.string(),
    /** Validade do access token em segundos, contada a partir da resposta. */
    expiresIn: z.number().int().positive(),
    user: authUserSchema,
});
//# sourceMappingURL=auth.js.map